import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CustomRoleService } from '../service/Admin.customRoleService'
import { toast } from '@app/components/ui/Toast/toast'
import type { UserCustomRole } from '@app/types'
import { AxiosError } from 'axios'
import {
  USERS_QUERY_KEY,
  USER_QUERY_KEY,
  USER_CUSTOM_ROLE_QUERY_KEY,
  LAYER_PERMISSIONS_QUERY_KEY,
} from '../utils/Admin.constants'

export const useFetchCustomUserRoles = () => {
  return useQuery<UserCustomRole[]>({
    queryKey: [USER_CUSTOM_ROLE_QUERY_KEY],
    queryFn: async () => {
      try {
        return await CustomRoleService.getUserCustomRoles()
      } catch (error: unknown) {
        if (error instanceof AxiosError && error.status === 404) {
          return []
        }
        throw error
      }
    },
  })
}

export const useUpdateCustomUserRole = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name, description }: { id: number; name: string; description: string }) =>
      CustomRoleService.updateUserCustomRole(id, name, description),
    onSuccess: () => {
      toast({
        description: `Role updated successfully.`,
        position: 'top-right',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: [USER_CUSTOM_ROLE_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [LAYER_PERMISSIONS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] })
    },
    onError: () =>
      toast({
        description: `Failed to update role.`,
        position: 'top-right',
        type: 'error',
      }),
  })
}

export const useCreateCustomUserRole = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ name, description }: { name: string; description: string }) =>
      CustomRoleService.createUserCustomRoles(name, description),
    onSuccess: () => {
      toast({
        description: `Role created successfully.`,
        position: 'top-right',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: [USER_CUSTOM_ROLE_QUERY_KEY] })
    },
    onError: () =>
      toast({
        description: `Failed to create role.`,
        position: 'top-right',
        type: 'error',
      }),
  })
}

export const useDeleteCustomUserRole = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: number }) => CustomRoleService.deleteUserCustomRoles(id),
    onSuccess: () => {
      toast({
        description: `Role deleted successfully.`,
        position: 'top-right',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: [USER_CUSTOM_ROLE_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [LAYER_PERMISSIONS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] })
    },
    onError: () =>
      toast({
        description: `Failed to delete role.`,
        position: 'top-right',
        type: 'error',
      }),
  })
}
